import { createStyles, Group, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
	AutomationJobResourceAIOutputResponse,
	AutomationUpdateEntityAIOutputIn,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Button, Icon, Text, Title } from '@repo/foundations';
import { compact, isNil, map, reverse } from 'lodash-es';
import { useState } from 'react';
import { useAuthUser } from '../../../api';
import { useIntegrationList } from '../../../api/hooks/integration';
import { useNestedBreadcrumbsList } from '../../../api/hooks/nestedBreadcrumbs';
import type { AutomationJob } from '../../../api/types/models/automation/automationJob';
import type { INestedBreadcrumbs } from '../../../api/types/models/nestedBreadcrumbs';
import type { IApiListResponse } from '../../../api/types/network/responses';
import { getBreadcrumbForEntity } from '../../../utils/breadcrumb';
import { ANALYTICS_EVENTS } from '../../../utils/constants/analytics';
import { RichEditor } from '../../RichEditor';
import { SecodaEntityIcon } from '../../SecodaEntity/SecodaEntityIcon';
import { AutomationJobAiOutputPropertySelector } from './AutomationJobAiOutputPropertySelector';

interface AutomationJobAiOutputModalProps {
	onCancel: (entityId: string) => void;
	onSubmit: (body: AutomationUpdateEntityAIOutputIn) => void;
	automationJob: AutomationJob;
	aiOutputResponse: AutomationJobResourceAIOutputResponse;
	onPrevClick: () => void;
	onNextClick: () => void;
	handleCompleteAutomationReview: (automationJobId: string) => void;
}

const useStyles = createStyles((theme) => ({
	sectionStack: {
		gap: theme.spacing['2xs'],
	},
	sectionTitle: {
		color: theme.other.getColor('text/secondary/default'),
	},
	content: {
		overflowY: 'scroll',
		height: '520px',
		scrollbarWidth: 'none', // Firefox
		msOverflowStyle: 'none', // Internet Explorer 10+
		'&::-webkit-scrollbar': {
			display: 'none', // WebKit browsers
		},
	},
}));

export function AutomationJobAiOutputModal({
	onCancel,
	onSubmit,
	automationJob,
	aiOutputResponse,
	onPrevClick,
	onNextClick,
	handleCompleteAutomationReview,
}: AutomationJobAiOutputModalProps) {
	const { classes } = useStyles();
	const { user, workspace } = useAuthUser();

	const [description, setDescription] = useState<string | undefined | null>(
		aiOutputResponse.ai_output.description
	);
	const [definition, setDefinition] = useState<string | undefined | null>(
		aiOutputResponse.ai_output.definition
	);
	const [tags, setTags] = useState<string[] | undefined | null>(
		aiOutputResponse.ai_output.tags
	);
	const [pii, setPii] = useState<boolean | undefined | null>(
		aiOutputResponse.ai_output.pii ?? null
	);

	const handleSubmit = async () => {
		const body: AutomationUpdateEntityAIOutputIn = {
			automation_job_id: automationJob.id,
			entity_id: aiOutputResponse.entity.id,
		};

		if (description) {
			body.description = description;
		}
		if (definition) {
			body.definition = definition;
		}
		if (tags) {
			body.tags = tags;
		}
		if (!isNil(pii)) {
			body.pii = pii;
		}

		try {
			onSubmit(body);

			showNotification({
				title: 'Updated AI output',
				message: `AI output updated for ${aiOutputResponse.entity.title}.`,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
		} catch (e) {
			showNotification({
				title: 'Error updating AI output',
				message: `Failed to update AI output for ${aiOutputResponse.entity.title}.`,
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		}
	};
	const { data: integrations } = useIntegrationList({
		options: {
			select: (res) => res.results,
		},
	});

	const { data: nestedBreadcrumbs } = useNestedBreadcrumbsList({
		filters: {
			id: aiOutputResponse.entity.id!,
		},
		options: {
			enabled: !isNil(aiOutputResponse.entity.id),
			select: (res: IApiListResponse<INestedBreadcrumbs>) =>
				reverse(res.results || []),
		},
	});

	const breadcrumb = getBreadcrumbForEntity({
		entity: aiOutputResponse.entity,
		nativeType: '',
		team: null,
		nestedBreadcrumbs,
		integrations,
		includeTeam: false,
	});

	const breadcrumbText = compact(map(breadcrumb, 'title')).join(' / ');

	return (
		<Stack spacing="md">
			<Text size="sm" color="text/secondary/default">
				{breadcrumbText}
			</Text>
			<Stack spacing="xl" className={classes.content}>
				{aiOutputResponse.entity.title && (
					<Group spacing="xs" noWrap>
						<SecodaEntityIcon entity={aiOutputResponse.entity} size={20} />
						<Title size="xl">{aiOutputResponse.entity.title}</Title>
					</Group>
				)}
				{(!isNil(pii) || tags) && (
					<Stack className={classes.sectionStack}>
						<Text size="sm" color="text/secondary/default">
							Properties
						</Text>
						<AutomationJobAiOutputPropertySelector
							entity={aiOutputResponse.entity}
							property={{ pii, tags }}
							onTagChange={setTags}
							onPiiChange={setPii}
						/>
					</Stack>
				)}
				{description && (
					<Stack className={classes.sectionStack}>
						<Text size="sm" color="text/secondary/default">
							Description
						</Text>
						<RichEditor
							readOnly={false}
							initialValue={description}
							onChangeCallback={setDescription}
							disableTopGap
						/>
					</Stack>
				)}
				{definition && (
					<Stack className={classes.sectionStack}>
						<Text size="sm" color="text/secondary/default">
							Documentation
						</Text>
						<RichEditor
							readOnly={false}
							initialValue={definition ?? ''}
							onChangeCallback={setDefinition}
							disableTopGap
						/>
					</Stack>
				)}
			</Stack>
			<Group spacing={8} position="right">
				<Button
					variant="tertiary"
					onClick={() => onCancel(aiOutputResponse.entity.id)}
				>
					Discard
				</Button>
				<Button variant="default" onClick={handleSubmit}>
					Approve update
				</Button>
				<Button
					variant="primary"
					onClick={() => {
						// Track approve all output event from modal
						trackEvent(
							ANALYTICS_EVENTS.AI_OUTPUT_APPROVE_ALL,
							{
								automation_id: automationJob.automation?.toString() || '',
								job_id: automationJob.id,
								affected_records_count:
									automationJob.affected_records_count?.toString() || '0',
								triggered_from: 'modal',
							},
							user,
							workspace
						);
						handleCompleteAutomationReview(automationJob.id);
					}}
				>
					Approve all output
				</Button>
			</Group>
		</Stack>
	);
}
